import { createContext, useContext } from "react"
import { RootStore } from "./root-store"

/**
 * Create a context we can use to
 * - Provide access to our stores from our root component
 * - Consume stores in our screens (or other components, though it's
 *   preferable to just connect screens)
 */
const RootStoreContext = createContext<RootStore>({} as RootStore)
const RefreshRootStoreContext = createContext<() => Promise<any>>(() => Promise.resolve({} as any))

/**
 * The provider our root component will use to expose the root store
 */
export const RootStoreProvider = RootStoreContext.Provider
export const RefreshRootStoreProvider = RefreshRootStoreContext.Provider

/**
 * A hook that screens can use to gain access to our stores, with
 * `const { someStore, someOtherStore } = useStores()`,
 * or less likely: `const rootStore = useStores()`
 */
export const useStores = () => useContext(RootStoreContext)
export const useRefreshStores = () => useContext(RefreshRootStoreContext)

export const useModelStoresModifying = () => {
  const {
    waterQualityMultiMeasurementStore,
    chemicalMultiMeasurementStore,
    microbiologyMultiMeasurementStore,
    planktonMultiMeasurementStore,
    treatmentStore,
    feedStore,
    fastingStore,
    samplingStore,
    harvestStore,
    mortalityStore,
    cycleDiseaseStore,
    feedingTrayControlStore,
  } = useStores();

  return [
    waterQualityMultiMeasurementStore.modifying,
    chemicalMultiMeasurementStore.modifying,
    microbiologyMultiMeasurementStore.modifying,
    planktonMultiMeasurementStore.modifying,
    treatmentStore.modifying,
    feedStore.modifying,
    fastingStore.modifying,
    samplingStore.modifying,
    harvestStore.modifying,
    mortalityStore.modifying,
    cycleDiseaseStore.modifying,
    feedingTrayControlStore.modifying,
  ].reduce((previousValue, currentValue) => previousValue && !currentValue, true)
}

export const useModelStoresStatus: () => {
  loading?: boolean|null|undefined,
  modifying?: boolean|null|undefined,
  storing?: boolean|null|undefined,
  updating?: boolean|null|undefined,
  deleting?: boolean|null|undefined,
} = () => {
  const {
    waterQualityMultiMeasurementStore,
    chemicalMultiMeasurementStore,
    microbiologyMultiMeasurementStore,
    planktonMultiMeasurementStore,
    treatmentStore,
    feedStore,
    fastingStore,
    samplingStore,
    harvestStore,
    mortalityStore,
    cycleDiseaseStore,
    feedingTrayControlStore,
  } = useStores();
  const modelStores = [
    waterQualityMultiMeasurementStore,
    chemicalMultiMeasurementStore,
    microbiologyMultiMeasurementStore,
    planktonMultiMeasurementStore,
    treatmentStore,
    feedStore,
    fastingStore,
    samplingStore,
    harvestStore,
    mortalityStore,
    cycleDiseaseStore,
    feedingTrayControlStore,
  ]

  const status = {};
  ['loading', 'modifying', 'storing', 'updating', 'deleting'].forEach((key) => {
    status[key] = modelStores.reduce((previousValue, currentValue) => (
      previousValue || currentValue[key]
    ), false)
  })
  return status;
}
